// Tournament data contracts (Phase 7 DT). The tournament corpus in
// `packages/engine/src/data/tournament/` validates against these. Authored at DT; consumed by ST1
// (push/fold resolver + ICM expansion), ST2 (scenario conditions), ST3 (ICM-aware grading).
//
// Design notes:
//   - Push/fold ranges are AUTHORED equilab strings (same form as range-set.ts), NOT redistributed
//     solver output. The DT validator parses each with the engine's `parseRange` and reports the
//     resulting combo-% + enforces monotonicity (wider as stacks shorten / position gets later) - the
//     cross-consistency guarantee. A cell carries no asserted percentage that could drift from its
//     string; the validator computes it.
//   - ICM presets carry payout FRACTIONS (relative - ICM math is scale-free) plus representative
//     per-stage stack snapshots so ST2 can seed a bubble / final-table / pay-jump spot and the DT
//     validator can prove each preset resolves through the engine `icmEquity` / `riskPremium` layer.
//   - Stage adjustments are MULTIPLIERS on the cash archetype params (vpip / 3bet / limp) - how the
//     15 shipped archetypes shift as stacks shorten and the bubble nears - never new archetypes.
//   - Tournament exploit rules reuse ExploitRuleSchema (exploit-rule.ts); they gate on the new
//     optional `when.tournamentStage`.
import { z } from 'zod';
import { ArchetypeIdSchema } from './archetype';
import { TournamentStageSchema } from './scenario';
import { IcmPressureSchema } from './buckets';
import { ReadLockSchema } from './read';
// ── Push/fold (Nash) ranges ──────────────────────────────────────────────────
/** Position buckets for the push/fold charts. EP = UTG/UTG1/UTG2 (6-max: LJ); MP = LJ/HJ (6-max:
 *  HJ); then CO/BTN/SB/BB exact. Bucketed (not exact seat) to match the range-set `{POS}_vs_{BUCKET}`
 *  convention and keep the short-stack corpus a teaching chart, not a 9×9 seat matrix. */
export const PUSHFOLD_POSITIONS = ['EP', 'MP', 'CO', 'BTN', 'SB', 'BB'];
export const PushFoldPositionSchema = z.enum(PUSHFOLD_POSITIONS);
/** Effective-stack bands (in BB) the push/fold charts are keyed on - the Nash-relevant short-stack
 *  ladder. Below ~3bb it is a pure any-two shove; above ~20-25bb push/fold gives way to regular
 *  open-raising (that zone is the cash charts + PF5 reconciliation). Keyed as strings so the JSON is
 *  human-legible and the coverage table reads cleanly. */
export const PUSHFOLD_STACK_BANDS = ['3bb', '5bb', '8bb', '10bb', '12bb', '15bb', '20bb'];
export const PushFoldStackBandSchema = z.enum(PUSHFOLD_STACK_BANDS);
/** One (position, band) cell. `range` is an equilab string (parsed + %-measured by the validator);
 *  the optional `note` records the teaching point. No asserted percentage - the validator computes
 *  it from the string so the two can never drift. */
export const PushFoldCellSchema = z.object({
    range: z.string(),
    note: z.string().optional(),
});
/** The shover's position class, which splits the call-off ranges (mirrors the engine `ShoverClass` in
 *  `tournament/pushfold.ts`): a wide late-position shove (`steal`) is called wider than a tight
 *  early-position shove (`early`). Declared in the schema so PF5's preflop push/fold provenance
 *  (preflop.ts `PreflopPushFoldProvenance`) can carry it as a versioned type. */
export const ShoverClassSchema = z.enum(['steal', 'early']);
/** Calling a shove: tighter than the jamming range, and split by the shover's position class -
 *  `vsSteal` (a wide late-position CO/BTN/SB shove ⇒ call wider) vs `vsEarly` (a tight EP/MP shove ⇒
 *  call tighter). Covers the two teaching cases without a full caller×shover matrix. */
export const PushFoldCallCellSchema = z.object({
    vsSteal: PushFoldCellSchema,
    vsEarly: PushFoldCellSchema,
});
/** A push/fold chart for one table size. `jam` = open-shove ranges (folded to hero) by position ×
 *  band; `callVsShove` = call-off ranges by caller position × band. Records are validated for the
 *  intended coverage (and monotonicity) by the DT validator, not by zod key-presence. */
export const PushFoldChartSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    /** citation basis for SOURCES.md - never a redistributed solver chart. */
    source: z.string(),
    tableSize: z.union([z.literal(6), z.literal(9)]),
    jam: z.record(PushFoldPositionSchema, z.record(PushFoldStackBandSchema, PushFoldCellSchema)),
    callVsShove: z.record(PushFoldPositionSchema, z.record(PushFoldStackBandSchema, PushFoldCallCellSchema)),
});
// ── ICM / payout-structure presets ───────────────────────────────────────────
/** A representative stack snapshot at a tournament stage - enough to seed a scenario (ST2) and to
 *  prove the preset resolves through the ICM layer (DT validator). `stacks` are chip counts for the
 *  players still in at that point; `payoutsInPlay` is the still-live prize ladder (fractions of the
 *  pool). heroStackRank is 1-based (1 = chip leader). */
export const IcmStageSnapshotSchema = z.object({
    stage: TournamentStageSchema,
    label: z.string(),
    playersLeft: z.number().int().positive(),
    stacks: z.array(z.number().positive()).min(2),
    heroStackRank: z.number().int().positive(),
    /** still-live prize ladder as fractions of the pool (need not sum to 1 - it is the tail of the full
     *  structure that is still contestable at this stage). */
    payoutsInPlay: z.array(z.number().nonnegative()).min(1),
    note: z.string().optional(),
});
/** A payout structure preset: field size + prize ladder + representative stage snapshots. Payouts are
 *  FRACTIONS of the pool (ICM math is scale-free). Buy-in is a display label only (no dollar amounts
 *  enter the strategy layer). */
export const PayoutStructureSchema = z.object({
    id: z.string(),
    label: z.string(),
    version: z.number().int(),
    source: z.string(),
    /** display only, e.g. '$100'. */
    buyInLabel: z.string(),
    fieldSize: z.number().int().positive(),
    paidPlaces: z.number().int().positive(),
    /** prize fractions of the pool, length === paidPlaces, monotonically non-increasing, summing ≈ 1. */
    payoutFractions: z.array(z.number().nonnegative()).min(1),
    /** flag the two structural extremes the ICM math cares about. */
    shape: z.enum(['standard', 'flat', 'topHeavy', 'satellite']),
    stages: z.array(IcmStageSnapshotSchema).min(1),
});
// ── Stage archetype adjustments ──────────────────────────────────────────────
/** A multiplier on a cash-archetype param. 1 = unchanged, <1 = tighter/less, >1 = wider/more. */
const MultSchema = z.number().positive();
/** How the whole field shifts at a stage (applied on top of each archetype's cash params). */
export const StageFieldShiftSchema = z.object({
    vpipScaleMult: MultSchema,
    threeBetScaleMult: MultSchema,
    limpBiasMult: MultSchema,
    /** qualitative jam/aggression tendency of the field at this stage. */
    jamTendency: z.enum(['minimal', 'low', 'moderate', 'high', 'extreme']),
    note: z.string(),
});
/** A per-archetype override at a stage (subset of the field - only archetypes that move notably). */
export const ArchetypeStageShiftSchema = z.object({
    archetypeId: ArchetypeIdSchema,
    vpipScaleMult: MultSchema,
    threeBetScaleMult: MultSchema,
    jamTendency: z.enum(['minimal', 'low', 'moderate', 'high', 'extreme']),
    note: z.string(),
});
export const StageAdjustmentSchema = z.object({
    stage: TournamentStageSchema,
    label: z.string(),
    description: z.string(),
    fieldShift: StageFieldShiftSchema,
    archetypeShifts: z.array(ArchetypeStageShiftSchema),
});
/** stage-adjustments.json as a versioned unit. */
export const StageAdjustmentTableSchema = z.object({
    version: z.number().int(),
    source: z.string(),
    stages: z.array(StageAdjustmentSchema),
});
// ── Tournament-condition read (ST3 - Axis B "read the condition") ─────────────────
/** The effective-stack bands as the user reads them: deep (regular poker) → mid → short (close to
 *  jamming) → pushFold (jam-or-fold depth). Mirrors the engine's `StackStage` (tournament/staging.ts);
 *  declared in the schema so the read-guess contract is a versioned type the UI and grader share. */
export const TOURNAMENT_STACK_STAGES = ['deep', 'mid', 'short', 'pushFold'];
export const TournamentStackStageSchema = z.enum(TOURNAMENT_STACK_STAGES);
/** What the user asserts about the tournament CONDITION at a decision (ST3 read axis). Each sub-read is
 *  optional - the user may read only the stage, only the stack depth, etc.; the grader scores whichever
 *  were guessed (untouched sub-reads are absent, never averaged in as neutral, mirroring the player-read
 *  posture). Graded against the scenario's scripted ground truth by ordinal adjacency (§7.4). */
export const ConditionReadGuessSchema = z.object({
    /** the tournament stage the user reads (early → headsUp). */
    guessedStage: TournamentStageSchema.optional(),
    /** the effective-stack band the user reads (deep → pushFold). */
    guessedStackStage: TournamentStackStageSchema.optional(),
    /** the ICM pressure the user reads (none / moderate / severe). */
    guessedPressure: IcmPressureSchema.optional(),
    /** free-form cues the user attached (memory hooks, unscored). */
    notes: z.array(z.string()).optional(),
    lock: ReadLockSchema,
});
//# sourceMappingURL=tournament.js.map